import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getCategoryState,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useEmblaCarousel } from "embla-carousel/react";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
    brandsToDisplay = "brandsToDisplay",
    numberOfCategoriesToDisplay = "numberOfCategoriesToDisplay",
    selectedCategoryIds = "selectedCategoryIds",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.brandsToDisplay]: "select-category";
        [fields.numberOfCategoriesToDisplay]: string;
        [fields.selectedCategoryIds]: string[];
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    pageSize: parseInt(ownProps.fields.numberOfCategoriesToDisplay || "0", 10),
    categoriesDataView: getCategoriesDataView(state),
    shouldLoadcategories: !getCategoriesDataView(state).value,
});

const mapDispatchToProps = {
    loadCategories,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext &
    OwnProps;

export interface CustomCarouselStyles {
    errorMessageText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    carouselSlideImageWrapper?: InjectableCss;
    carouselSlideTextWrapper?: InjectableCss;
}

export const CustomCarouselStyles: CustomCarouselStyles = {
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 10px;
        `,
    },
    mainContainer: { gap: 0 },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 2%;
                        max-width: 2%;
                    `,
                    css`
                        flex-basis: 2%;
                        max-width: 2%;
                    `,
                    css`
                        flex-basis: 4%;
                        max-width: 4%;
                    `,
                    css`
                        flex-basis: 4%;
                        max-width: 4%;
                    `,
                    css`
                        flex-basis: 2%;
                        max-width: 2%;
                    `,
                ])}
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 5px;
            background: #f8f8f8;
            z-index: 1;
            &:hover {
                background: ${getColor("primary")};
                opacity: 0.8;
                span {
                    color: #fff !important;
                }
            }
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 2%;
                        max-width: 2%;
                    `,
                    css`
                        flex-basis: 2%;
                        max-width: 2%;
                    `,
                    css`
                        flex-basis: 4%;
                        max-width: 4%;
                    `,
                    css`
                        flex-basis: 4%;
                        max-width: 4%;
                    `,
                    css`
                        flex-basis: 2%;
                        max-width: 2%;
                    `,
                ])}
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 5px;
            background: #f8f8f8;
            z-index: 1;
            &:hover {
                background: ${getColor("primary")};
                opacity: 0.8;
                span {
                    color: #fff !important;
                }
            }
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 96%;
                        max-width: 96%;
                    `,
                    css`
                        flex-basis: 96%;
                        max-width: 96%;
                    `,
                    css`
                        flex-basis: 92%;
                        max-width: 92%;
                    `,
                    css`
                        flex-basis: 92%;
                        max-width: 92%;
                    `,
                    css`
                        flex-basis: 96%;
                        max-width: 96%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 10px;
            display: flex;
            flex-direction: column;
        `,
    },
    carouselSlideImageWrapper: {
        css: css`
            border: #ccc 1px solid;
            height: 100%;
            justify-content: center;
        `,
    },
    carouselSlideTextWrapper: {
        css: css`
            justify-content: center;
            margin-top: 10px;
            span {
                font-weight: 600;
                color: #353432;
            }
        `,
    },
};

const styles = CustomCarouselStyles;

const CustomCarousel: React.FC<Props> = ({
    id,
    theme,
    shouldLoadcategories,
    categoriesDataView,
    fields,
    loadCategories,
    shellContext,
}) => {
    const afterCarousel = React.createRef<HTMLSpanElement>();
    React.useEffect(() => {
        if (shouldLoadcategories) {
            loadCategories();
        }
    }, [shouldLoadcategories]);
    const selectedCategoriesIds = fields.selectedCategoryIds;
    const allCategories = categoriesDataView.value;
    const selectedCategories = allCategories?.filter(el => {
        return selectedCategoriesIds.some(f => {
            return f === el.id;
        });
    });

    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = React.useState(0);
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const setCanScroll = () => {
        if (shellContext.isInShell) {
            return;
        }
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const getSlidesToScroll = React.useCallback(() => {
        const maxNumberOfColumns = 5;
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        if (window.innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (window.innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 2;
        } else if (window.innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 3;
        } else {
            localSlidesToScroll = 5;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [5]);

    const [slidesToScroll, setSlidesToScroll] = React.useState(getSlidesToScroll());

    const getDraggable = () => {
        if (typeof window === "undefined") {
            return false;
        }

        return window.innerWidth < theme.breakpoints.values[1];
    };

    const [draggable, setDraggable] = React.useState(getDraggable());

    React.useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);

        const newDraggable = getDraggable();
        setDraggable(newDraggable);
    }, [5, windowResizeTime]);

    React.useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    React.useEffect(() => {
        if (!embla || !selectedCategories || selectedCategories.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll,
            draggable,
            loop: selectedCategories.length > slidesToScroll,
        });
        setCanScroll();
    }, [embla, slidesToScroll, draggable, selectedCategories]);

    if (!selectedCategories || selectedCategories.length === 0) {
        return null;
    }

    const title = fields.title;

    return (
        <>
            <SkipNav
                text={translate("Skip Carousel")}
                extendedStyles={styles.skipCarouselButton}
                destination={afterCarousel}
            />
            <Typography {...styles.titleText}>{title}</Typography>
            <GridContainer {...styles.mainContainer} data-test-selector="Custom_Carousel">
                <GridItem {...styles.prevArrowGridItem}>
                    {selectedCategories.length > slidesToScroll && (
                        <Button
                            {...styles.prevArrowButton}
                            onClick={() => embla && embla.scrollPrev()}
                            disabled={!canScrollPrev}
                            data-test-selector="prevBtn"
                            title="Previous"
                        >
                            <ButtonIcon src={ChevronLeft} />
                        </Button>
                    )}
                </GridItem>
                <GridItem {...styles.carouselGridItem}>
                    <StyledWrapper {...styles.carouselWrapper}>
                        <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                            <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                {selectedCategories.map(category => (
                                    <StyledWrapper
                                        {...styles.carouselSlide}
                                        key={category.id}
                                        style={{ width: `calc(100% / ${slidesToScroll})` }}
                                    >
                                        <StyledWrapper
                                            {...styles.carouselSlideInner}
                                            id={`product_${category.id}`}
                                            data-test-selector="productContainer"
                                        >
                                            <Clickable href={category.path} {...styles.carouselSlideImageWrapper}>
                                                <LazyImage src={category.smallImagePath} />
                                            </Clickable>
                                            <StyledWrapper>
                                                <Clickable href={category.path} {...styles.carouselSlideTextWrapper}>
                                                    <Typography>{category.shortDescription}</Typography>
                                                </Clickable>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                ))}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.nextArrowGridItem}>
                    {selectedCategories.length > slidesToScroll && (
                        <Button
                            {...styles.nextArrowButton}
                            onClick={() => embla && embla.scrollNext()}
                            disabled={!canScrollNext}
                            data-test-selector="nextBtn"
                            title="Next"
                        >
                            <ButtonIcon src={ChevronRight} />
                        </Button>
                    )}
                </GridItem>
            </GridContainer>
            <span ref={afterCarousel} tabIndex={-1} />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(CustomCarousel))),
    definition: {
        group: "Categories",
        icon: "Image",
        displayName: "Category Custom Carousel",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.numberOfCategoriesToDisplay,
                displayName: "Number of categories to display",
                editorTemplate: "DropDownField",
                defaultValue: "12",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                    { displayName: "9", value: "9" },
                    { displayName: "10", value: "10" },
                    { displayName: "11", value: "11" },
                    { displayName: "12", value: "12" },
                    { displayName: "13", value: "13" },
                    { displayName: "14", value: "14" },
                    { displayName: "15", value: "15" },
                    { displayName: "16", value: "16" },
                    { displayName: "17", value: "17" },
                    { displayName: "18", value: "18" },
                ],
                fieldType: "General",
            },
            {
                name: fields.selectedCategoryIds,
                displayName: "Selected Categories",
                editorTemplate: "CategoriesField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 3,
            },
        ],
    },
};

export default widgetModule;
